<script setup lang="ts">
/**
 * MultiSelectDropdown
 *
 * A searchable, multi-select dropdown with tag chips.
 * Extracted from inline implementations in BulkTenantSync and MediaList.
 */
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

export interface MultiSelectOption {
    value: string
    label: string
    meta?: string
}

const props = withDefaults(
    defineProps<{
        modelValue: string[]
        options: MultiSelectOption[]
        placeholder?: string
        label?: string
        searchable?: boolean
        disabled?: boolean
    }>(),
    {
        placeholder: 'Select...',
        searchable: true,
        disabled: false,
    },
)

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const containerRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

const filteredOptions = computed(() => {
    if (!searchQuery.value.trim()) return props.options
    const q = searchQuery.value.toLowerCase()
    return props.options.filter(
        (o) =>
            o.label.toLowerCase().includes(q) ||
            (o.meta && o.meta.toLowerCase().includes(q)),
    )
})

const selectedLabels = computed(() => {
    const map = new Map(props.options.map((o) => [o.value, o]))
    return props.modelValue
        .map((v) => map.get(v))
        .filter(Boolean) as MultiSelectOption[]
})

function toggleOption(value: string) {
    const idx = props.modelValue.indexOf(value)
    if (idx >= 0) {
        emit(
            'update:modelValue',
            props.modelValue.filter((v) => v !== value),
        )
    } else {
        emit('update:modelValue', [...props.modelValue, value])
    }
}

function removeTag(value: string) {
    emit(
        'update:modelValue',
        props.modelValue.filter((v) => v !== value),
    )
}

function openDropdown() {
    if (props.disabled) return
    isOpen.value = true
    nextTick(() => inputRef.value?.focus())
}

function handleClickOutside(event: MouseEvent) {
    if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
        isOpen.value = false
        searchQuery.value = ''
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside, true)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true)
})
</script>

<template>
    <div ref="containerRef" class="msd" :class="{ 'msd--disabled': disabled }">
        <label v-if="label" class="msd__label">{{ label }}</label>

        <div class="msd__control" @click.stop="openDropdown">
            <div class="msd__tags">
                <span
                    v-for="item in selectedLabels"
                    :key="item.value"
                    class="msd__tag"
                >
                    {{ item.label }}
                    <button
                        class="msd__tag-remove"
                        @click.stop="removeTag(item.value)"
                    >×</button>
                </span>
                <input
                    v-if="searchable"
                    ref="inputRef"
                    v-model="searchQuery"
                    type="text"
                    class="msd__input"
                    :placeholder="modelValue.length === 0 ? placeholder : ''"
                    :disabled="disabled"
                    @focus="isOpen = true"
                />
                <span
                    v-else-if="modelValue.length === 0"
                    class="msd__placeholder"
                >{{ placeholder }}</span>
            </div>

            <svg
                class="msd__chevron"
                :class="{ 'msd__chevron--open': isOpen }"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
            >
                <path d="M6 9l6 6 6-6" />
            </svg>
        </div>

        <Transition name="msd-dropdown">
            <div v-if="isOpen" class="msd__dropdown">
                <div
                    v-for="option in filteredOptions"
                    :key="option.value"
                    :class="[
                        'msd__option',
                        { 'msd__option--selected': modelValue.includes(option.value) },
                    ]"
                    @click.stop="toggleOption(option.value)"
                >
                    <span class="msd__checkbox">
                        <svg
                            v-if="modelValue.includes(option.value)"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                        >
                            <path d="M20 6L9 17l-5-5" />
                        </svg>
                    </span>
                    <span class="msd__option-text">
                        <span class="msd__option-label">{{ option.label }}</span>
                        <span v-if="option.meta" class="msd__option-meta">{{ option.meta }}</span>
                    </span>
                </div>
                <p v-if="filteredOptions.length === 0" class="msd__empty">
                    No options found
                </p>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.msd {
    position: relative;
}

.msd--disabled {
    opacity: 0.5;
    pointer-events: none;
}

.msd__label {
    display: block;
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
    margin-bottom: 0.25rem;
}

.msd__control {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    padding: 0.25rem 0.375rem;
    cursor: text;
    min-height: 34px;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.msd__control:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.1);
}

.msd__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    flex: 1;
    min-width: 0;
}

.msd__tag {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
    color: var(--text-primary);
    white-space: nowrap;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.msd__tag-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    padding: 0;
    border: none;
    background: none;
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    border-radius: 2px;
    flex-shrink: 0;
}

.msd__tag-remove:hover {
    color: var(--color-error);
    background: rgba(255, 71, 87, 0.1);
}

.msd__input {
    flex: 1;
    min-width: 80px;
    border: none;
    background: none;
    outline: none;
    font-size: var(--text-sm);
    color: var(--text-primary);
    padding: 0.125rem 0;
}

.msd__input::placeholder {
    color: var(--text-disabled);
}

.msd__placeholder {
    font-size: var(--text-sm);
    color: var(--text-disabled);
}

.msd__chevron {
    width: 16px;
    height: 16px;
    color: var(--text-muted);
    flex-shrink: 0;
    transition: transform 0.15s;
}

.msd__chevron--open {
    transform: rotate(180deg);
}

.msd__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    max-height: 200px;
    overflow-y: auto;
}

.msd__option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.625rem;
    cursor: pointer;
    transition: background 0.1s;
}

.msd__option:hover {
    background: var(--bg-hover);
}

.msd__option--selected {
    background: rgba(18, 192, 221, 0.06);
}

.msd__checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: 1.5px solid var(--border-color);
    border-radius: 3px;
    flex-shrink: 0;
    transition: all 0.15s;
}

.msd__option--selected .msd__checkbox {
    background: var(--color-primary);
    border-color: var(--color-primary);
}

.msd__checkbox svg {
    width: 12px;
    height: 12px;
    color: white;
}

.msd__option-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.msd__option-label {
    font-size: var(--text-sm);
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.msd__option-meta {
    font-size: var(--text-xs);
    color: var(--text-muted);
}

.msd__empty {
    padding: 0.75rem;
    text-align: center;
    font-size: var(--text-xs);
    color: var(--text-muted);
}

/* Dropdown animation */
.msd-dropdown-enter-active,
.msd-dropdown-leave-active {
    transition: all 0.15s ease;
}

.msd-dropdown-enter-from,
.msd-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
